import { Injectable, inject, signal } from '@angular/core';
import type { WorkflowPayload } from '../models/canonical.types';
import type { ResumeOutcome, RunState } from '../models/run-state.types';
import { WorkflowGraphWalker } from '../engine/workflow-graph-walker';

@Injectable({ providedIn: 'root' })
export class WorkflowRunStore {
  private readonly walker = inject(WorkflowGraphWalker);
  readonly state = signal<RunState | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  async start(workflowKey: string, input: WorkflowPayload = {}): Promise<RunState> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const next = await this.walker.start(workflowKey, input);
      this.state.set(next);
      return next;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start workflow';
      this.error.set(message);
      throw err;
    } finally {
      this.loading.set(false);
    }
  }

  async resume(outcome: ResumeOutcome = {}): Promise<RunState> {
    const current = this.state();
    if (!current) throw new Error('No active run');
    this.loading.set(true);
    this.error.set(null);
    try {
      const next = await this.walker.resume(current.instanceId, outcome);
      this.state.set(next);
      return next;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resume workflow';
      this.error.set(message);
      throw err;
    } finally {
      this.loading.set(false);
    }
  }

  async hydrate(instanceId: string): Promise<RunState | null> {
    const existing = await this.walker.getState(instanceId);
    if (existing) this.state.set(existing);
    return existing;
  }

  readActiveInstanceId(workflowKey: string): Promise<string | null> {
    return this.walker.readActiveInstanceId(workflowKey);
  }

  listStoredInstanceIds(workflowKey: string): Promise<string[]> {
    return this.walker.listStoredInstanceIds(workflowKey);
  }
}
